import { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { MajorTopic } from '../../types/usageMetrics';

interface Props {
  topics: MajorTopic[];
}

export default function MajorTopicDiscussed({ topics }: Props) {
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Sort data based on count
  const sortedData = [...topics].sort((a, b) => {
    if (sortOrder === 'desc') {
      return b.count - a.count;
    } else {
      return a.count - b.count;
    }
  });

  // Toggle sort order
  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
  };

  // Modern, vibrant color palette with good contrast
  const COLORS = [
    '#6366f1', // Indigo
    '#8b5cf6', // Violet  
    '#06b6d4', // Cyan
    '#10b981', // Emerald
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#ec4899', // Pink
    '#84cc16', // Lime
    '#f97316', // Orange
    '#3b82f6', // Blue
    '#14b8a6', // Teal
    '#a855f7', // Purple
  ];

  // Define types for tooltip props
  interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{
      value: number;
      payload: MajorTopic;
    }>;
    label?: string;
  }

  // Define types for label props
  interface LabelProps {
    cx: number;
    cy: number;
    midAngle: number;
    innerRadius: number;
    outerRadius: number;
    percent: number;
    title: string;
  }

  // Custom tooltip component
  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white px-4 py-3 shadow-lg rounded-lg border border-gray-200">
          <p className="font-semibold text-gray-800 mb-1">{data.payload.title}</p>
          <p className="text-sm text-gray-600">
            <span className="font-medium">{data.value}</span> discussions
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {((data.value / topics.reduce((sum, topic) => sum + topic.count, 0)) * 100).toFixed(1)}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom label function for better readability
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, title }: LabelProps): JSX.Element | null => {
    if (percent < 0.05) return null; // Don't show labels for very small slices
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="text-xs font-semibold"
        style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.7)' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="w-full p-6 bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-gray-800 tracking-tight">Major Topics Discussed</h3>
        <button
          onClick={toggleSortOrder}
          className="px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 rounded-lg hover:from-gray-100 hover:to-gray-200 transition-all duration-200 font-medium text-sm shadow-sm border border-gray-200"
        >
          Sort {sortOrder === 'desc' ? 'Ascending' : 'Descending'}
        </button>
      </div>

      {topics.length === 0 ? (
        <div className="h-80 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
            </div>
            <p className="text-gray-500 text-lg font-medium">No major topic discussions found</p>
            <p className="text-gray-400 text-sm mt-1">Data will appear here once topics are discussed</p>
          </div>
        </div>
      ) : (
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <Pie
                data={sortedData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={100}
                innerRadius={40} // Creates a donut effect
                fill="#8884d8"
                dataKey="count"
                nameKey="title"
                animationDuration={800}
                animationBegin={0}
                stroke="white"
                strokeWidth={2}
              >
                {sortedData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]}
                    className="hover:opacity-80 transition-opacity duration-200"
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                wrapperStyle={{
                  paddingLeft: '20px',
                  width: '180px',
                  fontSize: '14px',
                  lineHeight: '1.4'
                }}
                iconType="circle"
                formatter={(value: string, entry: any) => (
                  <span className="text-gray-700 font-medium">
                    {value.length > 25 ? `${value.slice(0, 25)}...` : value}
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      
      {topics.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Total Topics: <span className="font-semibold text-gray-800">{topics.length}</span></span>
            <span>Total Discussions: <span className="font-semibold text-gray-800">{topics.reduce((sum, topic) => sum + topic.count, 0)}</span></span>
          </div>
        </div>
      )}
    </div>
  );
}